import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Tooltip,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  CloudUpload as UploadIcon,
  PhotoLibrary as GalleryIcon,
  Visibility as PreviewIcon,
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  FiberManualRecord as DotIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Edit as EditIcon,
  Reorder as ReorderIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Platform-specific carousel limits
const PLATFORM_LIMITS = {
  instagram: {
    maxItems: 10,
    minItems: 2,
    maxCaptionLength: 2200,
    supportedFormats: ['image/jpeg', 'image/png', 'video/mp4'],
    maxFileSize: 100 * 1024 * 1024, // 100MB
    aspectRatios: ['1:1', '4:5', '16:9'],
    defaultAspectRatio: '1:1'
  },
  tiktok: {
    maxItems: 35,
    minItems: 2,
    maxCaptionLength: 4000,
    supportedFormats: ['image/jpeg', 'image/png', 'image/gif'],
    maxFileSize: 50 * 1024 * 1024, // 50MB
    aspectRatios: ['9:16', '3:4', '1:1'],
    defaultAspectRatio: '9:16'
  }
};

const CarouselContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.primary[50]} 0%, ${theme.palette.secondary[50]} 100%)`,
}));

const CarouselItem = styled(Card)(({ theme, isDragging }) => ({
  position: 'relative',
  cursor: 'grab',
  transition: 'all 0.2s ease',
  transform: isDragging ? 'rotate(5deg) scale(1.05)' : 'none',
  boxShadow: isDragging ? theme.shadows[8] : theme.shadows[2],
  '&:hover': {
    transform: isDragging ? 'rotate(5deg) scale(1.05)' : 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 400,
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const PreviewDots = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: theme.spacing(2),
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5),
  cursor: 'grab',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
}));

const ItemNumber = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  zIndex: 2,
}));

const CarouselMediaManager = ({
  platform = 'instagram',
  initialItems = [],
  onChange,
  onPreview,
  disabled = false,
  maxHeight = 600,
  compact = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);
  
  // Get platform-specific limits
  const limits = PLATFORM_LIMITS[platform] || PLATFORM_LIMITS.instagram;
  
  // State
  const [items, setItems] = useState(initialItems || []);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Auto-save and validation
  useEffect(() => {
    validateCarousel();
    if (onChange) {
      onChange(items);
    }
  }, [items, onChange]);

  const validateCarousel = useCallback(() => {
    const newErrors = [];
    
    // Check item count limits
    if (items.length < limits.minItems) {
      newErrors.push(`${platform} carousels require at least ${limits.minItems} items`);
    }
    if (items.length > limits.maxItems) {
      newErrors.push(`${platform} carousels can have at most ${limits.maxItems} items`);
    }
    
    // Check individual items
    items.forEach((item, index) => {
      if (item.caption && item.caption.length > limits.maxCaptionLength) {
        newErrors.push(`Item ${index + 1}: Caption exceeds ${limits.maxCaptionLength} characters`);
      }
      
      if (item.file && item.file.size > limits.maxFileSize) {
        newErrors.push(`Item ${index + 1}: File size exceeds ${limits.maxFileSize / (1024 * 1024)}MB limit`);
      }
      
      if (item.file && !limits.supportedFormats.includes(item.file.type)) {
        newErrors.push(`Item ${index + 1}: Unsupported file format. Use ${limits.supportedFormats.join(', ')}`);
      }
    });
    
    setErrors(newErrors);
    return newErrors.length === 0;
  }, [items, limits, platform]);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newItems = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size and type
        if (file.size > limits.maxFileSize) {
          console.warn(`File ${file.name} exceeds size limit`);
          continue;
        }
        
        if (!limits.supportedFormats.includes(file.type)) {
          console.warn(`File ${file.name} has unsupported format`);
          continue;
        }
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        const newItem = {
          id: `item-${Date.now()}-${i}`,
          file,
          url: previewUrl,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          caption: '',
          altText: '',
          order: items.length + newItems.length,
          aspectRatio: limits.defaultAspectRatio,
        };
        
        newItems.push(newItem);
        
        // Stop if we would exceed the limit
        if (items.length + newItems.length >= limits.maxItems) {
          break;
        }
      }
      
      setItems(prev => [...prev, ...newItems]);
      setShowAddDialog(false);
      
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    
    // Update order values
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index
    }));
    
    setItems(updatedItems);
  };

  const handleRemoveItem = (itemId) => {
    setItems(prev => {
      const filtered = prev.filter(item => item.id !== itemId);
      // Clean up preview URL
      const removedItem = prev.find(item => item.id === itemId);
      if (removedItem && removedItem.url && removedItem.url.startsWith('blob:')) {
        URL.revokeObjectURL(removedItem.url);
      }
      return filtered.map((item, index) => ({ ...item, order: index }));
    });
    
    // Adjust preview index if necessary
    if (previewIndex >= items.length - 1) {
      setPreviewIndex(Math.max(0, items.length - 2));
    }
  };

  const handleUpdateItem = (itemId, updates) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  const handlePreviewNavigation = (direction) => {
    if (direction === 'next') {
      setPreviewIndex(prev => (prev + 1) % items.length);
    } else {
      setPreviewIndex(prev => (prev - 1 + items.length) % items.length);
    }
  };

  const renderCarouselItem = (item, index, isDragging = false) => (
    <CarouselItem isDragging={isDragging}>
      <ItemNumber label={index + 1} size="small" />
      <DragHandle>
        <DragIcon fontSize="small" />
      </DragHandle>
      
      <CardMedia
        component={item.type === 'video' ? 'video' : 'img'}
        height="200"
        image={item.url}
        alt={item.altText || `Carousel item ${index + 1}`}
        controls={item.type === 'video'}
        sx={{
          objectFit: 'cover',
          backgroundColor: 'grey.100'
        }}
      />
      
      <CardContent sx={{ p: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          placeholder={`Caption for item ${index + 1}...`}
          value={item.caption}
          onChange={(e) => handleUpdateItem(item.id, { caption: e.target.value })}
          inputProps={{ maxLength: limits.maxCaptionLength }}
          helperText={`${item.caption.length}/${limits.maxCaptionLength} characters`}
          size="small"
        />
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit details">
            <IconButton 
              size="small" 
              onClick={() => setEditingItem(item)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview">
            <IconButton 
              size="small" 
              onClick={() => {
                setPreviewIndex(index);
                setShowPreview(true);
              }}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Tooltip title="Remove item">
          <IconButton 
            size="small" 
            color="error"
            onClick={() => handleRemoveItem(item.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </CarouselItem>
  );

  const renderPreviewDialog = () => (
    <Dialog
      open={showPreview}
      onClose={() => setShowPreview(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: 'black', color: 'white' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h6">
          Carousel Preview - {platform.charAt(0).toUpperCase() + platform.slice(1)}
        </Typography>
        <IconButton onClick={() => setShowPreview(false)} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {items.length > 0 && (
          <PreviewContainer>
            <CardMedia
              component={items[previewIndex]?.type === 'video' ? 'video' : 'img'}
              image={items[previewIndex]?.url}
              controls={items[previewIndex]?.type === 'video'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
            
            {/* Navigation arrows */}
            {items.length > 1 && (
              <>
                <IconButton
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                  }}
                  onClick={() => handlePreviewNavigation('prev')}
                >
                  <BackIcon />
                </IconButton>
                
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                  }}
                  onClick={() => handlePreviewNavigation('next')}
                >
                  <ForwardIcon />
                </IconButton>
                
                {/* Dots indicator */}
                <PreviewDots>
                  {items.map((_, index) => (
                    <IconButton
                      key={index}
                      size="small"
                      onClick={() => setPreviewIndex(index)}
                      sx={{
                        color: index === previewIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                        p: 0.5,
                        minWidth: 'auto',
                      }}
                    >
                      <DotIcon fontSize="small" />
                    </IconButton>
                  ))}
                </PreviewDots>
              </>
            )}
          </PreviewContainer>
        )}
        
        {/* Caption display */}
        {items[previewIndex]?.caption && (
          <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2">
              {items[previewIndex].caption}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  const renderAddDialog = () => (
    <Dialog
      open={showAddDialog}
      onClose={() => setShowAddDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Add Media to Carousel
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={limits.supportedFormats.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: 'none' }}
          />
          
          <Button
            variant="contained"
            size="large"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ mb: 2, minWidth: 200 }}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            startIcon={<GalleryIcon />}
            onClick={() => {
              // Handle gallery selection
              setShowAddDialog(false);
            }}
            sx={{ mb: 2, minWidth: 200, ml: 2 }}
          >
            From Gallery
          </Button>
          
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>{platform.charAt(0).toUpperCase() + platform.slice(1)} Requirements:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`${limits.minItems}-${limits.maxItems} items allowed`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ImageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Formats: ${limits.supportedFormats.join(', ')}`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Max file size: ${limits.maxFileSize / (1024 * 1024)}MB`}
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setShowAddDialog(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" component="h3">
            Carousel Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {platform.charAt(0).toUpperCase() + platform.slice(1)} carousel with {items.length} of {limits.maxItems} items
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {items.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(true)}
              size="small"
            >
              Preview
            </Button>
          )}
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            disabled={disabled || items.length >= limits.maxItems}
            size="small"
          >
            Add Media
          </Button>
        </Box>
      </Box>

      {/* Platform info and errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Carousel Items */}
      {items.length === 0 ? (
        <CarouselContainer>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No carousel items yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add {limits.minItems} or more media files to create a carousel post
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
              size="large"
            >
              Add First Item
            </Button>
          </Box>
        </CarouselContainer>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="carousel-items" direction={isMobile ? 'vertical' : 'horizontal'}>
            {(provided) => (
              <Grid
                container
                spacing={2}
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ 
                  maxHeight: maxHeight,
                  overflowY: 'auto',
                  overflowX: isMobile ? 'visible' : 'auto',
                  pb: 2
                }}
              >
                {items.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          transform: provided.draggableProps.style?.transform,
                        }}
                      >
                        {renderCarouselItem(item, index, snapshot.isDragging)}
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Progress indicator */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Carousel Progress
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {items.length}/{limits.maxItems} items
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(items.length / limits.maxItems) * 100}
          color={items.length >= limits.minItems ? 'success' : 'warning'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {/* Dialogs */}
      {renderPreviewDialog()}
      {renderAddDialog()}
    </Box>
  );
};

export default CarouselMediaManager; 